import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import {
  apiListDeviationCategories,
  type DeviationCategory,
} from "api/simulation/deviationCategories";

export type DeviationCategoryOption = {
  value: string;
  label: string;
  category: DeviationCategory;
};

const PAGE_SIZE = 20;

export type DeviationCategoryValueField = "id" | "name";

function categoryToOption(
  category: DeviationCategory,
  valueField: DeviationCategoryValueField,
): DeviationCategoryOption {
  return {
    value: valueField === "id" ? category.id : category.name,
    label: category.name,
    category,
  };
}

function matchesSearch(category: DeviationCategory, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (!query) {
    return true;
  }
  return (
    category.name.toLowerCase().includes(query) ||
    category.code.toLowerCase().includes(query) ||
    (category.description?.toLowerCase().includes(query) ?? false)
  );
}

export type UseDeviationCategoryOptionsParams = {
  /** Texto de búsqueda ya debounced (p. ej. 500 ms). */
  search: string;
  /** Evita fetch hasta que el menú del select esté abierto. */
  enabled?: boolean;
  /** `name` para motivo general (coverage API); `id` para desviaciones por período. */
  valueField?: DeviationCategoryValueField;
};

export function useDeviationCategoryOptions(
  params: UseDeviationCategoryOptionsParams,
) {
  const { search, enabled = true, valueField = "name" } = params;

  const query = useInfiniteQuery(
    ["deviation-categories", "select-options", PAGE_SIZE],
    ({ pageParam = 1 }) =>
      apiListDeviationCategories({
        page: pageParam as number,
        page_size: PAGE_SIZE,
      }),
    {
      enabled,
      staleTime: 60_000,
      retry: 1,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.next ? allPages.length + 1 : undefined,
    },
  );

  const allCategories = useMemo(() => {
    const seen = new Set<string>();
    const merged: DeviationCategory[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const row of page.results) {
        if (row.is_active === false || seen.has(row.id)) {
          continue;
        }
        seen.add(row.id);
        merged.push(row);
      }
    }
    return merged;
  }, [query.data?.pages]);

  const options = useMemo(() => {
    return allCategories
      .filter((row) => matchesSearch(row, search))
      .map((row) => categoryToOption(row, valueField));
  }, [allCategories, search, valueField]);

  return {
    options,
    allCategories,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}
